import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

import css from './CreatePaymentModal.module.css';
import { ordersService } from '../../../../services/orders-services';
import { ordersActions } from '../../../../store/slices/orders-slices';
import { getOptionPaymentMethod, getOptionSelectPaymentStatus } from '../../../../helpers';

const CreatePaymentModal = ({ setIsShowCreatePaymentModal, orderId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      amount: '',
      paymentMethod: '',
      paymentStatus: '',
      stripePaymentIntentId: '',
    },
  });

  const [isPaymentMethodDropdownOpen, setIsPaymentMethodDropdownOpen] = useState(false);
  const [isPaymentStatusDropdownOpen, setIsPaymentStatusDropdownOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);

  const paymentMethodOptions = getOptionPaymentMethod(t);
  const paymentStatusOptions = getOptionSelectPaymentStatus(t);

  const closeModal = () => {
    setIsShowCreatePaymentModal(false);
  };

  const onSubmit = async (data) => {
    try {
      if (!selectedPaymentMethod) {
        toast.error(t('payment-method-required'));
        return;
      }

      if (!selectedPaymentStatus) {
        toast.error(t('payment-status-required'));
        return;
      }

      const paymentData = {
        amount: parseFloat(data.amount),
        paymentMethod: selectedPaymentMethod.forBack,
        paymentStatus: selectedPaymentStatus.forBack,
      };

      // Only include stripePaymentIntentId if it's provided
      if (data.stripePaymentIntentId && data.stripePaymentIntentId.trim()) {
        paymentData.stripePaymentIntentId = data.stripePaymentIntentId.trim();
      }

      await ordersService.createPayment(orderId, paymentData);

      toast.success(t('payment-created-successfully'));

      // Small delay to ensure database transaction is fully committed
      await new Promise((resolve) => setTimeout(resolve, 100));

      dispatch(ordersActions.changeTrigger());
      closeModal();
    } catch (error) {
      console.error(t('modal-toast-creating-error'), error);
      toast.error(t('modal-toast-creating-error'));
    }
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    setIsPaymentMethodDropdownOpen(false);
  };

  const handlePaymentStatusSelect = (status) => {
    setSelectedPaymentStatus(status);
    setIsPaymentStatusDropdownOpen(false);
  };

  // Show Stripe Payment Intent ID field only when CARD is selected
  const showStripeField = selectedPaymentMethod?.forBack === 'CARD';

  return (
    <div className={css.overlay} onClick={closeModal}>
      <div className={css.modal} onClick={(e) => e.stopPropagation()}>
        <div className={css.header}>
          <div className={css.title}>{t('create-payment')}</div>
          <div className={css.closeButton} onClick={closeModal}>
            &times;
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={css.form}>
          {/* Amount Field */}
          <div className={css.formGroup}>
            <label htmlFor="amount" className={css.label}>
              {t('Amount')} *
            </label>
            <input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              className={css.input}
              {...register('amount', {
                required: t('amount-required'),
                min: {
                  value: 0.01,
                  message: t('amount-must-be-positive'),
                },
              })}
            />
            {errors.amount && (
              <span className={css.error}>{errors.amount.message}</span>
            )}
          </div>

          {/* Payment Method Dropdown */}
          <div className={css.formGroup}>
            <label className={css.label}>{t('payment-method')} *</label>
            <div className={css.customSelect}>
              <div
                className={css.selectDisplay}
                onClick={() =>
                  setIsPaymentMethodDropdownOpen(!isPaymentMethodDropdownOpen)
                }
              >
                {selectedPaymentMethod
                  ? selectedPaymentMethod.name
                  : t('select-payment-method')}
                <div className={css.arrow}></div>
              </div>

              {isPaymentMethodDropdownOpen && (
                <div className={css.dropdown}>
                  {paymentMethodOptions.map((method) => (
                    <div
                      key={method.forBack}
                      className={css.dropdownOption}
                      onClick={() => handlePaymentMethodSelect(method)}
                    >
                      <span>{method.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Payment Status Dropdown */}
          <div className={css.formGroup}>
            <label className={css.label}>{t('payment-status')} *</label>
            <div className={css.customSelect}>
              <div
                className={css.selectDisplay}
                onClick={() =>
                  setIsPaymentStatusDropdownOpen(!isPaymentStatusDropdownOpen)
                }
              >
                {selectedPaymentStatus
                  ? selectedPaymentStatus.name
                  : t('select-payment-status')}
                <div className={css.arrow}></div>
              </div>

              {isPaymentStatusDropdownOpen && (
                <div className={css.dropdown}>
                  {paymentStatusOptions.map((status) => (
                    <div
                      key={status.forBack}
                      className={css.dropdownOption}
                      onClick={() => handlePaymentStatusSelect(status)}
                    >
                      <span>{status.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Stripe Payment Intent ID (conditional) */}
          {showStripeField && (
            <div className={css.formGroup}>
              <label htmlFor="stripePaymentIntentId" className={css.label}>
                {t('stripe-payment-intent-id')} ({t('optional')})
              </label>
              <input
                id="stripePaymentIntentId"
                type="text"
                className={css.input}
                {...register('stripePaymentIntentId')}
              />
            </div>
          )}

          {/* Submit Button */}
          <div className={css.formActions}>
            <button
              type="button"
              className={css.cancelButton}
              onClick={closeModal}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className={css.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('creating') : t('create-payment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { CreatePaymentModal };

